import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';

import { AtomBubble } from './AtomBubble';
import type { AtomSaysPayload } from '../../services/websocketService';

/**
 * ATOM, SPEAKING.
 *
 * ---------------------------------------------------------------------------
 * Every word Atom "said" in this app until now was a hardcoded string:
 *
 *     <AtomBubble msg="Shuffling the wheel…" />
 *     <AtomBubble msg={`Letter ${letter} — Names, Places, Animals, Things. Go!`} />
 *
 * The same two sentences, every round, every game, forever. Meanwhile the server
 * has had four written personalities and a `/api/chat/ai` endpoint the whole
 * time, and `grep "chat/ai" src` returned nothing at all. Nobody had ever
 * called it.
 * ---------------------------------------------------------------------------
 *
 * THIS COMPONENT IS DELIBERATELY DUMB — it does not touch the socket.
 *
 * The first version subscribed to `atom_says` itself, on mount. That is wrong,
 * and a real browser caught it in about four seconds: the server emits the
 * round-start line the instant the round starts, and this component does not
 * MOUNT until the letter wheel finishes spinning 1.5 seconds later. It
 * subscribed to an event that had already happened and rendered nothing, every
 * round, in a component whose entire purpose is to render that event. The tally
 * line was lost the same way — emitted on `roundEnded`, while the screen was
 * still on `pencils` and this component did not yet exist.
 *
 * So the subscription lives in GameScreen, which is mounted for the whole game
 * and cannot miss anything, and this component just draws what it is handed.
 *
 * THE ONE RULE THAT SURVIVED: **ATOM'S SILENCE MUST BE INVISIBLE.** The line may
 * never come — no API key, spent budget, no Atom at the table. That is not an
 * error state and it gets no spinner, no placeholder, no reserved empty box.
 * There is no bubble, and then there is a bubble.
 */
export function AtomSays({
  /** What Atom said. `null` means Atom has not spoken — render nothing. */
  line,
  /**
   * What to draw when Atom has not spoken.
   *
   * NOT a loading state — there is no such thing here. It is for the room with
   * no Atom in it: four humans round a kitchen table, nobody to do the talking,
   * and a screen that still needs to say which letter came up.
   */
  fallback = null,
  className = '',
}: {
  line: Pick<AtomSaysPayload, 'text' | 'kind'> | null;
  fallback?: React.ReactNode;
  className?: string;
}) {
  if (!line?.text) {
    return fallback ? <div className={className}>{fallback}</div> : null;
  }

  return (
    <div className={className}>
      <AnimatePresence mode="wait">
        <motion.div
          // Keyed on the text, so a second line in the same phase actually
          // animates in rather than silently swapping under the reader.
          key={line.text}
          initial={{ opacity: 0, y: 6, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -4 }}
          transition={{ duration: 0.22, ease: 'easeOut' }}
        >
          <AtomBubble msg={line.text} kind={line.kind || 'standard'} />
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
